/**
 * Token-text collision handling for `RenameScreen.tsx`'s field-token chips (Monday 12645331451,
 * item 2 — the same field NAME can legitimately exist on both the org's asset-scope and
 * segment-scope configured views, per `PORTAL-INTERFACE.md` §5.4: they're different views, so a
 * "GameID" on each is a genuinely different value to resolve, not a duplicate). Before this, both
 * scopes' chips inserted the identical `{FieldName}` text, so `RenameScreen.tsx`'s
 * `computeFieldTokens` (keyed by that literal text) could only ever record ONE of the two — the
 * other silently lost its field identity. Pulled into its own module (mirrors `../mapping/addField.ts`'s
 * "small pure function, own test" convention) so the collision rule is unit-testable without
 * mounting the screen.
 */

/** Segment-scope tokens are the ones qualified on a collision, never asset — this keeps every
 *  already-saved template's bare `{FieldName}` usage resolving to the same field it always did
 *  (the common case, no collision) while still making a genuinely ambiguous pair distinguishable. */
const SEGMENT_COLLISION_PREFIX = "Timed:";

/** Field names present on BOTH scopes' view field lists — the only names that actually need
 *  disambiguating. */
export function collidingFieldNames(
  assetFieldNames: Iterable<string>,
  segmentFieldNames: Iterable<string>
): Set<string> {
  const asset = new Set(assetFieldNames);
  const colliding = new Set<string>();
  Array.from(segmentFieldNames).forEach((name) => {
    if (asset.has(name)) {
      colliding.add(name);
    }
  });
  return colliding;
}

/** The literal `{...}` token text for one field on one scope's view — `fieldName` unchanged unless
 *  it's a segment-scope field colliding with an asset-scope field of the same name (see
 *  `SEGMENT_COLLISION_PREFIX`'s doc comment above), in which case it's prefixed so it becomes a
 *  distinct string (and therefore a distinct `fieldTokens` map key). */
export function tokenNameFor(
  fieldName: string,
  scope: "asset" | "segment",
  colliding: ReadonlySet<string>
): string {
  return scope === "segment" && colliding.has(fieldName)
    ? `${SEGMENT_COLLISION_PREFIX}${fieldName}`
    : fieldName;
}
